import React, { useState, useEffect } from 'react';
import { ImageReorder } from './ImageReorder';
import { Book, TagCategory, Tag, CreateBookData, ValidationError, BookImage } from '../types';
import { booksApi, tagsApi } from '../services/api';
import './BookForm.css';

// Estilos inline para melhor legibilidade
const styles = {
  label: { display: 'block', marginBottom: '8px', fontWeight: 'bold' as const, color: '#333', fontSize: '14px' },
  input: { width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box' as const, fontSize: '14px', backgroundColor: '#fff', color: '#333' },
  textarea: { width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box' as const, fontSize: '14px', backgroundColor: '#fff', color: '#333', resize: 'vertical' as const },
  field: { marginBottom: '20px' },
  checkbox: { display: 'flex', alignItems: 'center', gap: '10px', color: '#333', fontSize: '14px' },
  button: { padding: '12px 24px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' as const },
  primaryBtn: { background: '#007bff', color: 'white' },
  secondaryBtn: { background: '#6c757d', color: 'white' },
  cancelBtn: { background: '#dc3545', color: 'white' },
  modal: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: 'white', borderRadius: '12px', padding: '30px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '15px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' },
  imagePreview: { display: 'flex', gap: '10px', flexWrap: 'wrap' as const, marginTop: '10px' },
  imageThumb: { width: '80px', height: '80px', objectFit: 'cover' as const, borderRadius: '6px', border: '2px solid #ddd' },
  imageContainer: { position: 'relative' as const },
  removeImageBtn: { position: 'absolute' as const, top: '-8px', right: '-8px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '12px' }
};

// Componente BookForm com melhorias
interface BookFormProps {
  book?: Book;
  onSubmit?: (book: Book) => void;
  onCancel?: () => void;
  isModal?: boolean;
}

export function BookForm({ book, onSubmit, onCancel, isModal }: BookFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [publicationYear, setPublicationYear] = useState('');
  const [physicallyAvailable, setPhysicallyAvailable] = useState(false);
  const [isAwarded, setIsAwarded] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<TagCategory[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [existingImages, setExistingImages] = useState<BookImage[]>([]);
  const isEditMode = !!book;

  useEffect(() => {
    tagsApi.getCategories().then(setCategories).catch(console.error);
    tagsApi.getAllByCategory().then(data => {
      const tags: Tag[] = [];
      data.forEach(item => {
        if (item.tags) {
          tags.push(...item.tags);
        }
      });
      setAllTags(tags);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (book) {
      setTitle(book.title);
      setDescription(book.description);
      setPublicationYear(book.publicationYear?.toString() || '');
      setPhysicallyAvailable(book.physicallyAvailable);
      setIsAwarded(book.isAwarded || false);
      setSelectedTagIds(book.tags?.map(t => t.id) || []);
      setExistingImages(book.images || []);
    } else {
      setTitle(''); setDescription(''); setPublicationYear(''); setPhysicallyAvailable(false); setIsAwarded(false); setSelectedTagIds([]); setExistingImages([]);
    }
  }, [book]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewImages(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreviews(prev => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeNewImage = (index: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageId: number) => {
    if (book) {
      try {
        await booksApi.removeImage(book.id, imageId);
        setExistingImages(prev => prev.filter(img => img.id !== imageId));
        setMessage('Imagem removida!');
        setTimeout(() => setMessage(null), 2000);
      } catch {
        setMessage('Erro ao remover imagem');
      }
    }
  };

  const handleImageReorder = async (reorderedImages: BookImage[]) => {
    setExistingImages(reorderedImages);

    if (book) {
      try {
        const imageIds = reorderedImages.map(img => img.id);
        await booksApi.reorderImages(book.id, imageIds);
        setMessage('Ordem das imagens atualizada!');
        setTimeout(() => setMessage(null), 2000);
      } catch {
        setMessage('Erro ao reordenar imagens');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: ValidationError[] = [];
    if (!title.trim()) errs.push({ field: 'title', message: 'Título obrigatório' });
    if (!description.trim()) errs.push({ field: 'description', message: 'Descrição obrigatória' });
    setErrors(errs);
    if (errs.length > 0) return;

    setIsSubmitting(true);
    try {
      const data = { title: title.trim(), description: description.trim(), publicationYear: publicationYear ? parseInt(publicationYear) : undefined, physicallyAvailable, isAwarded, tagIds: selectedTagIds };
      const saved = isEditMode && book ? await booksApi.update(book.id, data, newImages) : await booksApi.create(data as CreateBookData, newImages);
      setMessage(isEditMode ? 'Livro atualizado!' : 'Livro cadastrado!');
      if (!isEditMode) { setTitle(''); setDescription(''); setPublicationYear(''); setPhysicallyAvailable(false); setSelectedTagIds([]); setNewImages([]); setImagePreviews([]); }
      setTimeout(() => { if (onSubmit) onSubmit(saved); }, 500);
    } catch { setMessage('Erro ao salvar'); }
    finally { setIsSubmitting(false); }
  };

  const formContent = (
    <form onSubmit={handleSubmit}>
      {message && <div style={{padding: '12px', background: message.includes('Erro') ? '#f8d7da' : '#d4edda', marginBottom: '20px', borderRadius: '6px', color: message.includes('Erro') ? '#721c24' : '#155724'}}>{message}</div>}
      
      <div style={styles.field}>
        <label style={styles.label}>Título *</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} style={styles.input} disabled={isSubmitting} placeholder="Digite o título do livro" />
        {errors.find(e => e.field === 'title') && <span style={{color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block'}}>{errors.find(e => e.field === 'title')?.message}</span>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Descrição *</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} style={styles.textarea} disabled={isSubmitting} placeholder="Digite a descrição do livro" />
        {errors.find(e => e.field === 'description') && <span style={{color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block'}}>{errors.find(e => e.field === 'description')?.message}</span>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Ano de Publicação</label>
        <input type="number" value={publicationYear} onChange={e => setPublicationYear(e.target.value)} style={{...styles.input, width: '150px'}} disabled={isSubmitting} placeholder="Ex: 2024" />
      </div>

      <div style={styles.field}>
        <label style={styles.checkbox}><input type="checkbox" checked={physicallyAvailable} onChange={e => setPhysicallyAvailable(e.target.checked)} disabled={isSubmitting} /> Livro disponível fisicamente na empresa</label>
      </div>

      <div style={styles.field}>
        <label style={styles.checkbox}><input type="checkbox" checked={isAwarded} onChange={e => setIsAwarded(e.target.checked)} disabled={isSubmitting} /> 🏆 Livro Premiado</label>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Imagens do Livro</label>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px'}}>
          <label style={{...styles.button, ...styles.secondaryBtn, display: 'inline-block', cursor: 'pointer'}}>
            📷 Escolher Imagens
            <input type="file" accept="image/*" multiple onChange={handleImageSelect} disabled={isSubmitting} style={{display: 'none'}} />
          </label>
          <span style={{color: '#333', fontSize: '14px'}}>{newImages.length > 0 ? `${newImages.length} arquivo(s) selecionado(s)` : 'Nenhum arquivo selecionado'}</span>
        </div>
        {existingImages.length > 0 && (
          <ImageReorder
            images={existingImages}
            onReorder={handleImageReorder}
            onRemove={removeExistingImage}
          />
        )}
        {imagePreviews.length > 0 && (
          <div style={styles.imagePreview}>
            {imagePreviews.map((preview, i) => (
              <div key={`new-${i}`} style={styles.imageContainer}>
                <img src={preview} alt={`Nova ${i+1}`} style={styles.imageThumb} />
                <button type="button" onClick={() => removeNewImage(i)} style={styles.removeImageBtn}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Tags</label>
        {categories.map(cat => {
          const tags = allTags.filter(t => t.categoryId === cat.id);
          if (!tags.length) return null;
          return (
            <div key={cat.id} style={{marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{color: '#333', display: 'block', marginBottom: '8px'}}>{cat.displayName}</strong>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px'}}>
                {tags.map(tag => (
                  <label key={tag.id} style={{...styles.checkbox, background: selectedTagIds.includes(tag.id) ? '#e3f2fd' : 'white', padding: '6px 12px', borderRadius: '20px', border: '1px solid #ddd'}}>
                    <input type="checkbox" checked={selectedTagIds.includes(tag.id)} onChange={() => setSelectedTagIds(prev => prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id])} disabled={isSubmitting} />
                    {tag.name}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee'}}>
        {onCancel && <button type="button" onClick={onCancel} disabled={isSubmitting} style={{...styles.button, ...styles.cancelBtn}}>Cancelar</button>}
        <button type="submit" disabled={isSubmitting} style={{...styles.button, ...styles.primaryBtn}}>{isSubmitting ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Cadastrar Livro'}</button>
      </div>
    </form>
  );

  if (isModal && onCancel) {
    return (
      <div style={styles.modal} onClick={onCancel}>
        <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={{margin: 0, color: '#333'}}>{isEditMode ? '✏️ Editar Livro' : '📚 Cadastrar Novo Livro'}</h2>
            <button onClick={onCancel} style={styles.closeBtn}>×</button>
          </div>
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div style={{padding: '25px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '25px'}}>
      <h2 style={{marginTop: 0, color: '#333', marginBottom: '25px'}}>📚 Cadastrar Novo Livro</h2>
      {formContent}
    </div>
  );
}

export default BookForm;
